/**
 * `useIdleTimeout` centralizes idle detection (warning + timeout) using `react-idle-timer`.
 *
 * Intended usage:
 * - When `isWarning` becomes true, show a UI warning message.
 * - When `onTimeout` fires (idle for `timeoutMin`), redirect/log out.
 * - When user becomes active again, `onContinue` fires and warning can be dismissed.
 * - Min means minutes
 */
import { useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import type { EventsType, IIdleTimer } from 'react-idle-timer';

const timeMultiplier = 60_000; // 1 minute = 60,000 milliseconds

export type UseIdleTimeoutOptions = {
  enabled?: boolean;
  timeoutMin?: number;
  promptBeforeIdleMin?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
  onContinue?: () => void;
};

const promptEvents: EventsType[] = ['keydown', 'touchstart', 'touchmove'];

export const useIdleTimeout = (options: UseIdleTimeoutOptions = {}) => {
  const {
    enabled = false,
    timeoutMin = 20, // default value cause must be bigger then promptBeforeIdle. otherwise - ERROR
    promptBeforeIdleMin = 0,
    onWarning,
    onTimeout,
    onContinue
  } = options;

  const intervalRef = useRef<NodeJS.Timeout>();

  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0); // active only during warning period (for rendering optimization)

  const { getRemainingTime } = useIdleTimer({
    timeout: timeoutMin * timeMultiplier,
    promptBeforeIdle: promptBeforeIdleMin * timeMultiplier,
    onPrompt: () => {
      startTimer();
      setIsWarning(true);
      onWarning?.();
    },
    onIdle: () => {
      stopTimer();
      setIsWarning(false);
      onTimeout?.();
    },
    onAction: (e?: Event, idleTimer?: IIdleTimer) => {
      if (
        idleTimer?.isPrompted() &&
        promptEvents.includes(e?.type as EventsType)
      ) {
        idleTimer.activate();
        onContinue?.();
      }
    },
    onActive: (_?: Event, idleTimer?: IIdleTimer) => {
      if (idleTimer?.isPrompted()) {
        stopTimer();
        setIsWarning(false);
      }
    },
    eventsThrottle: 200,
    debounce: 1000,
    startOnMount: true,
    disabled: !enabled,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'scroll'
    ]
  });

  const startTimer = () => {
    if (intervalRef.current) return;

    const tick = () => {
      const ms = getRemainingTime();
      setRemainingTime(ms);
    };

    tick(); // start immediately
    intervalRef.current = setInterval(tick, 1000);
  };

  const stopTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = undefined;
  };

  return { isWarning, remainingTime };
};
